import React from "react";
import type { ApolloClient } from "@apollo/client";
import moment from "moment";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { Card, Col, Form, Row } from "react-bootstrap";
import { push } from "store/actions/router";
import { debounce, isEmpty, map } from "lodash";
import { getPaginatedCompanies } from "actions/adminActions";
import { createLoadingSelector } from "store/selectors";
import { adminConstants } from "actions/types";
import { EmployerClosed, getStatusLabel } from "statics/states";
import { FiUsers } from "react-icons/fi";
import { Link } from "react-router-dom";

import IconTableHeader from "components/IconTableHeader";
import CopyIcon from "components/CopyIcon";
import IconMessageCard from "components/IconMessageCard";
import IconSpinner from "components/IconSpinner";
import IconTable from "components/IconTable";
import type { IconTableColumn } from "components/IconTable";
import Paginator from "components/Paginator";
import type { Company } from "types/models";
import type { RootState } from "types/state";

import "./Employers.scss";

// Row shape produced by `_getEmployersData` and consumed by IconTable.
interface EmployerRow {
  companyId: Company["id"];
  companyName: Company["name"];
  companyDBA: Company["dba"];
  currentStatus: string;
  isDisabled: boolean;
  createdAt: string;
}

const stateFilters = [
  { value: "ACTIVE", label: "Active" },
  { value: "INACTIVE", label: "Inactive" },
  { value: "PENDING_KYB", label: "Pending KYB" },
  { value: "PENDING_KYB_APPROVAL", label: "Pending Approval" },
  { value: "KYB_YELLOW_PATH", label: "Pending Yellowpath Docs" },
  { value: "DENIED", label: "Denied" },
];

const isLoadingSelector = createLoadingSelector(
  adminConstants.GET_PAGINATED_COMPANIES
);

const mapStateToProps = (state: RootState) => {
  return {
    isFetching: isLoadingSelector(state),
    // admin slice is still `unknown[]` in RootState — cast at the boundary
    companies: state.admin.companies as Company[],
    companiesCount: state.admin.companiesCount,
  };
};

const mapDispatchToProps = {
  push,
  getPaginatedCompanies,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type Props = WithApolloClient<ConnectedProps<typeof connector>>;

interface State {
  limit: number;
  offset: number;
  search: string;
  initialFetching: boolean;
  page: number;
  stateFilter: string;
}

class Employers extends React.PureComponent<Props, State> {
  // Set by the search input's ref callback below.
  _textInput: HTMLInputElement | null = null;

  constructor(props: Props) {
    super(props);

    this.state = {
      limit: 50,
      offset: 0,
      search: "",
      initialFetching: true,
      page: 0,
      stateFilter: "",
    };
  }

  componentDidMount() {
    void this._getPaginatedCompanies().then(() => {
      this.setState({ initialFetching: false });
    });
  }

  _onPageChange = ({ selected }: { selected: number }) => {
    const offset = selected * this.state.limit;

    this.setState({ page: selected, offset }, () =>
      this._getPaginatedCompanies()
    );
  };

  _getPaginatedCompanies = () => {
    return this.props.getPaginatedCompanies(
      this.props.client as ApolloClient<object>,
      {
        limit: this.state.limit,
        offset: this.state.offset,
        search: this.state.search,
        stateFilter: this.state.stateFilter,
      }
    );
  };

  _onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState(
      {
        search: e.target.value,
        offset: 0,
        page: 0,
      },
      () => this._getPaginatedCompanies()
    );
  };

  _debounceOnChange = debounce(this._onSearchChange, 500);

  _getEmployersData = (): EmployerRow[] => {
    return this.props.companies.map((company) => {
      // currentState is `string | null | undefined`; getStatusLabel handles
      // null at runtime (it has a `case null:`), so cast to satisfy the sig.
      const status = getStatusLabel(company.currentState as string);
      const commonDateFormat = "MMM Do YYYY";
      return {
        companyId: company.id,
        companyName: company.name,
        companyDBA: company.dba,
        currentStatus: status,
        isDisabled: company.currentState === EmployerClosed,
        createdAt: moment(company.createdAt).format(commonDateFormat),
      };
    });
  };

  _buildEmployersColumns = (): IconTableColumn<EmployerRow>[] => {
    const columns: IconTableColumn<EmployerRow>[] = [
      {
        label: "Company Name",
        key: "companyName",
      },

      {
        label: "Company DBA",
        key: "companyDBA",
      },
      {
        label: "Company ID",
        key: "companyId",
        customComponent: (props) => (
          <>
            {props.companyId}
            <CopyIcon copyText={props.companyId} label="Company ID" />
          </>
        ),
      },
      {
        label: "Company State",
        key: "currentStatus",
        customComponent: (props) =>
          props.isDisabled ? (
            <span className="employer-disabled-pill">Disabled</span>
          ) : (
            <>{props.currentStatus}</>
          ),
      },

      {
        label: "Created Date",
        key: "createdAt",
      },

      {
        customComponent: (props) => {
          return (
            <div>
              <Link to={`/employers/${props.companyId}`} className="icon-link">
                View Info
              </Link>
            </div>
          );
        },
      },
    ];
    return columns;
  };

  _handleStateFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    this.setState(
      {
        stateFilter: e.target.value,
        offset: 0,
        page: 0,
      },
      () => this._getPaginatedCompanies()
    );
  };

  _clearFilters = () => {
    // ref is always set by the time the clear button is reachable — preserve
    // the original unconditional access (would throw if unset, as before).
    this._textInput!.value = "";
    this.setState(
      {
        search: "",
        stateFilter: "",
        offset: 0,
      },
      () => this._getPaginatedCompanies()
    );
  };

  render() {
    const columns = this._buildEmployersColumns();
    const data = this._getEmployersData();

    if (this.state.initialFetching) {
      return <IconSpinner />;
    }

    const showBlankSlate = isEmpty(this.props.companies);
    return (
      <div className="mega-container">
        <section className="page-title-wrap">
          <article className="text-cell">
            <h1 className="page-title">Employers</h1>

            <div className="main-content">
              <Card>
                {this.props.isFetching && <IconSpinner centered />}

                <Row>
                  <Col md={6}>
                    <div
                      style={{ paddingLeft: 15, paddingTop: 15 }}
                      className="search-bar"
                    >
                      <Form.Label>Search by name or id</Form.Label>

                      <Form.Control
                        name="search"
                        placeholder="search"
                        onChange={this._debounceOnChange}
                        ref={(node: HTMLInputElement | null) => {
                          this._textInput = node;
                        }}
                      />
                    </div>
                  </Col>
                </Row>

                <Row>
                  <Col md={4}>
                    <div
                      style={{ paddingLeft: 15, paddingBottom: 15 }}
                      className="state-filter"
                    >
                      <Form.Label>Filter by state</Form.Label>
                      <Form.Select
                        name="stateFilter"
                        value={this.state.stateFilter}
                        onChange={this._handleStateFilterChange}
                      >
                        <option value="">None</option>
                        {map(stateFilters, (filter) => (
                          <option key={filter.label} value={filter.value}>
                            {filter.label}
                          </option>
                        ))}
                      </Form.Select>
                    </div>
                  </Col>
                </Row>
                {showBlankSlate && (
                  <div style={{ marginTop: 12 }}>
                    <IconMessageCard
                      header={
                        "There were no companies found matching your search or filters"
                      }
                      subheader="Would you like to clear filters?"
                      icon={
                        <FiUsers color="white" stroke="#60A4BF" size={16} />
                      }
                      actionText={"Clear Filters"}
                      onClick={this._clearFilters}
                    />
                  </div>
                )}

                {!showBlankSlate && (
                  <>
                    <IconTableHeader
                      tableHeader="Employers"
                      tableCount={this.props.companiesCount}
                    />
                    <IconTable columns={columns} data={data} />
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        paddingBottom: 15,
                      }}
                    >
                      <Paginator
                        onChange={this._onPageChange}
                        pageCount={this.props.companiesCount / this.state.limit}
                        page={this.state.page}
                      />
                    </div>
                  </>
                )}
              </Card>
            </div>
          </article>
        </section>
      </div>
    );
  }
}

export default connector(withApollo<Props>(Employers));
